import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { bookingActionFlags } from './detail-view/bookingActionRules';
import { BookingActionWorkspace } from './detail-view/BookingActionWorkspace';
import { BookingDetailTabs } from './detail-view/BookingDetailTabs';
import { BookingHero } from './detail-view/BookingHero';
import {
  heroBadges,
  heroMeta,
  heroSource,
  heroTitle,
  stepSubs,
} from './detail-view/bookingHeroModel';
import { BookingLoadState, BookingStaleNote } from './detail-view/BookingLoadState';
import { BookingStayAside } from './detail-view/BookingStayAside';
import { BookingTabContent } from './detail-view/BookingTabContent';
import {
  bookingDetailTabFromSearch,
  type BookingDetailTab,
} from './detail-view/bookingDetailTabModel';
import { channelName, isOta } from './detail-view/helpers';
import { overviewTodos, todoCounts } from './detail-view/overviewTodos';
import { OwnerProtectedNote } from './detail-view/OwnerProtectedNote';
import { buildProgress } from './detail-view/progressModel';
import { useBookingWorkspace } from './detail-view/useBookingWorkspace';

/**
 * Buchungs-Arbeitsraum (Board „Buchung“): Wald-Kopf mit Pfad bis zum Gast, Status, Schritten und
 * Reitern; darunter Aufenthalt und Geld, der Inhalt des Reiters und die Aktionen. Eigentümer
 * (Board „BuchungEigentuemer“) sehen Zeitraum, Status und Preis – ohne Reiter, die nur „geschützt“ sagen.
 */
export function BookingDetailPage() {
  const { id } = useParams({ from: '/_admin/bookings/$id' });
  const search = useSearch({ from: '/_admin/bookings/$id' });
  const navigate = useNavigate();
  const ws = useBookingWorkspace(id);
  const booking = ws.booking.data;
  if (!booking) {
    return (
      <BookingLoadState
        error={ws.booking.error}
        paused={ws.booking.fetchStatus === 'paused'}
        onRetry={() => void ws.booking.refetch()}
        retrying={ws.booking.isFetching}
      />
    );
  }
  const activeTab = ws.manageable ? bookingDetailTabFromSearch(search.tab) : 'overview';
  const changeTab = (tab: BookingDetailTab) =>
    void navigate({
      to: '/bookings/$id',
      params: { id },
      search: { tab: tab === 'overview' ? undefined : tab },
    });
  const title = heroTitle(booking, ws.guestName, ws.canSeeGuests, ws.guestState);
  const source = heroSource(booking);
  const steps = buildProgress({
    booking,
    onboardingComplete: ws.onboardingComplete,
    invoiceState: ws.invoiceState,
  });
  // Ohne Aktion keine leere dritte Spalte – Eigentümer und Buchungen, an denen nichts mehr geht.
  const hasActions = bookingActionFlags(booking, ws.manageable, ws.apartments.length).any;
  const sourceNote = isOta(booking)
    ? `Daten verwaltet in ${channelName(booking.channel)}${ws.manageable ? ' · Apartment in StayOn änderbar' : ''}`
    : null;

  return (
    <div className="pb-[calc(6rem+env(safe-area-inset-bottom))] xl:pb-8">
      <BookingHero
        title={title}
        badges={heroBadges(booking)}
        meta={heroMeta(booking, ws.propertyName, ws.canSeeGuests)}
        source={source}
        sourceNote={sourceNote}
        steps={
          ws.manageable
            ? steps
            : steps.filter((step) => step.id === 'confirmed' || step.id === 'stay')
        }
        stepSubs={stepSubs(booking, source.label, ws.onboardingComplete, ws.invoiceState)}
        tabs={
          ws.manageable ? (
            <BookingDetailTabs
              active={activeTab}
              counts={todoCounts(overviewTodos(booking, ws.onboardingComplete, ws.invoiceState))}
              onChange={changeTab}
            />
          ) : undefined
        }
      />
      {ws.booking.isError && (
        <BookingStaleNote
          onRetry={() => void ws.booking.refetch()}
          retrying={ws.booking.isFetching}
        />
      )}
      <div
        className={
          hasActions
            ? 'grid gap-5 xl:grid-cols-[250px_minmax(0,1fr)_260px]'
            : 'grid gap-5 xl:grid-cols-[250px_minmax(0,1fr)]'
        }
      >
        <BookingStayAside
          booking={booking}
          propertyName={ws.propertyName}
          canSeeGuests={ws.canSeeGuests}
          onOpenMoney={ws.manageable ? () => changeTab('billing') : undefined}
        />
        {ws.manageable ? (
          <section
            role="tabpanel"
            id={`booking-panel-${activeTab}`}
            aria-labelledby={`booking-tab-${activeTab}`}
            className="min-w-0"
          >
            <BookingTabContent
              active={activeTab}
              api={ws.api}
              booking={booking}
              guest={ws.guest}
              guestState={ws.guestState}
              guestName={ws.guestName ?? title}
              invoices={ws.invoices.data ?? null}
              invoicesFailed={ws.invoices.isError}
              apartments={ws.apartments}
              onboardingComplete={ws.onboardingComplete}
              invoiceState={ws.invoiceState}
              onChangeTab={changeTab}
            />
          </section>
        ) : (
          <OwnerProtectedNote />
        )}
        <BookingActionWorkspace
          booking={booking}
          guestName={ws.guestName ?? `Buchung ${booking.bookingReference}`}
          guestPhone={ws.canSeeGuests ? (ws.guest.data?.phone ?? booking.guestPhone ?? null) : null}
          propertyName={ws.propertyName}
          manageable={ws.manageable}
          properties={ws.apartments}
        />
      </div>
    </div>
  );
}
